import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { DELETE, BACKSPACE, TAB, ESCAPE, ENTER, NUMPAD_PERIOD, PERIOD } from '@angular/cdk/keycodes';
import { UntypedFormControl } from '@angular/forms';
import { filter } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Directive({
  selector: '[miqOnlyNumbers]',
  standalone: false,
})
export class OnlyNumbersDirective implements OnInit, OnDestroy {
  @Input() allowedKeyCodes: number[] = [];
  @Input() restrictedKeyCodes: number[] = [];
  @Input() formControl: UntypedFormControl;
  @Input() restrictPaste = true;

  inputElement: HTMLInputElement;

  constructor(public el: ElementRef) {
    this.inputElement = el.nativeElement;
  }
  ngOnDestroy(): void {
    // Required by until destroy
  }
  ngOnInit(): void {
    this.formControl?.valueChanges
      .pipe(
        untilDestroyed(this),
        filter(() => this.allowedKeyCodes.includes(189))
      )
      .subscribe(() => this.allowNegativeCharAtBeginning());
  }

  private allowNegativeCharAtBeginning() {
    const value = this.formControl.value?.toString().replace(/\-/g, '');
    if (this.formControl.value?.toString().startsWith('-')) {
      this.formControl.setValue('-'.concat(value), { emitEvent: false });
      return;
    }
    this.formControl?.setValue(value, { emitEvent: false });
  }

  // Allow typing numbers only
  @HostListener('keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
    if (this.restrictedKeyCodes.includes(e.keyCode)) {
      e.preventDefault();
      return;
    }
    if (
      //46, 8, 9, 27, 13, 110, 190
      [DELETE, BACKSPACE, TAB, ESCAPE, ENTER, PERIOD, NUMPAD_PERIOD].includes(e.keyCode) ||
      // Allow: Ctrl+A
      (e.keyCode === 65 && (e.ctrlKey || e.metaKey)) ||
      // Allow: Ctrl+C
      (e.keyCode === 67 && (e.ctrlKey || e.metaKey)) ||
      // Allow: Ctrl+V
      (e.keyCode === 86 && (e.ctrlKey || e.metaKey)) ||
      // Allow: Ctrl+X
      (e.keyCode === 88 && (e.ctrlKey || e.metaKey)) ||
      // Allow: home, end, left, right
      (e.keyCode >= 35 && e.keyCode <= 39)
    ) {
      // let it happen, don't do anything
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
      if (!this.allowedKeyCodes.includes(e.keyCode)) {
        e.preventDefault();
      }
    }
  }

  #isValidNumberFromPaste(e: ClipboardEvent): boolean {
    const clipboardData = e.clipboardData?.getData('text');
    return typeof clipboardData === 'string' && !isNaN(Number(clipboardData)) && Number(clipboardData) > 0;
  }
  // Prevent pasting into the input field
  @HostListener('paste', ['$event']) blockPaste(e: ClipboardEvent) {
    if (this.restrictPaste || !this.#isValidNumberFromPaste(e)) {
      e.preventDefault();
    }
  }
}
